"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";

/**
 * The shared countdown primitive — Phase 07b.
 *
 * WHAT WAS THERE. `AuctionShowcaseClient` ran its own `useCountdown` with a
 * private `setInterval(1000)`, and the `/active-bid` ranking grid rendered up
 * to 24 `NFTCard`s each running an INDEPENDENT `setInterval(1000)` of their own
 * (`components/ui/NFTCard.tsx`) — the exact "per-card 1s interval across a
 * grid" risk named in `pages/AUCTIONS.md` §24. Twenty-four timers firing in the
 * same tick, each triggering its own state update and re-render, is the jank
 * class this codebase has already fixed twice elsewhere.
 *
 * WHAT THIS IS. One clock (`useSharedNow`), shared by every countdown on a
 * page. A grid mounts it ONCE and threads the resulting `nowMs` down to each
 * card; a single detail page mounts it once for its one countdown. The maths
 * (`computeCountdown`) and the announcement-threshold logic
 * (`crossedThreshold`) are pure and exported separately so they are testable
 * without a DOM or a timer.
 *
 * WHY ANNOUNCEMENTS ARE SEPARATE FROM THE TICK. A countdown that announces
 * every second is unusable with a screen reader (`pages/AUCTIONS.md` §23).
 * `crossedThreshold` answers a narrower question — "did the remaining time
 * just cross one of the four announced boundaries (1h / 10m / 1m / ended)
 * between the previous tick and this one" — so a caller announces at most
 * four times over an auction's entire remaining life, never once a second.
 */

export type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
  isZero: boolean;
};

/**
 * A running clock as the reader reads it, one rule for every countdown on the
 * site: Persian digits and «روز» on /fa («۲ روز ۰۴:۱۱:۰۹»), `2d 04:11:09` on
 * /en; the days only when there are days. The figures font follows the digits
 * (`--v2-num` is Estedad under RTL), so writing the digits right is the fix.
 *
 * `today`: under a day left, the days' slot says «امروز» / "Today" instead of
 * going blank — the auction cards, where a row of «۲ روز …» sits beside it.
 */
export function formatClock(c: Pick<CountdownParts, "days" | "hours" | "minutes" | "seconds">, isFa: boolean, {
  today = false
}: {
  today?: boolean;
} = {}): string {
  const two = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false
  });
  const hms = `${two.format(c.hours)}:${two.format(c.minutes)}:${two.format(c.seconds)}`;
  if (c.days <= 0) return today ? `${isFa ? "امروز" : "Today"} ${hms}` : hms;
  return isFa ? `${new Intl.NumberFormat("fa-IR").format(c.days)} روز ${hms}` : `${c.days}d ${hms}`;
}

/** Pure. `targetMs`/`nowMs` are both epoch milliseconds. */
export function computeCountdown(targetMs: number, nowMs: number): CountdownParts {
  const remaining = Math.max(0, targetMs - nowMs);
  const totalSeconds = Math.floor(remaining / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor(totalSeconds % 86400 / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60,
    totalMs: remaining,
    isZero: remaining <= 0
  };
}
export type CountdownThreshold = "1h" | "10m" | "1m" | "ended";
const THRESHOLDS: {
  id: CountdownThreshold;
  ms: number;
}[] = [{
  id: "1h",
  ms: 60 * 60_000
}, {
  id: "10m",
  ms: 10 * 60_000
}, {
  id: "1m",
  ms: 60_000
}, {
  id: "ended",
  ms: 0
}];

/**
 * Null unless the remaining time crossed exactly one boundary between the two
 * ticks — a `setInterval` firing late (a backgrounded tab) can skip straight
 * past a boundary; this still reports it once rather than never, and rather
 * than reporting every boundary skipped over in the same call.
 */
export function crossedThreshold(previousRemainingMs: number, currentRemainingMs: number): CountdownThreshold | null {
  if (currentRemainingMs >= previousRemainingMs) return null;
  for (const {
    id,
    ms
  } of THRESHOLDS) {
    if (previousRemainingMs > ms && currentRemainingMs <= ms) return id;
  }
  return null;
}

/**
 * One `setInterval` for as many countdowns as a caller has, active only while
 * `active` is true (e.g. "this page has at least one live/upcoming item").
 * Returns the current epoch ms; callers derive every countdown's parts from
 * this single value via `computeCountdown`.
 */
export function useSharedNow(active, t0) {
  const $ = _c(4);
  const intervalMs = t0 === undefined ? 1000 : t0;
  const [now, setNow] = useState(_temp);
  let t1;
  let t2;
  if ($[0] !== active || $[1] !== intervalMs) {
    t1 = () => {
      if (!active) {
        return;
      }
      const id = setInterval(() => setNow(Date.now()), intervalMs);
      return () => clearInterval(id);
    };
    t2 = [active, intervalMs];
    $[0] = active;
    $[1] = intervalMs;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return now;
}
function _temp() {
  return Date.now();
}